import { Session } from '../types';
import { env } from '../config/environment';
import { normalizeTimestamp, migrateTimestamps, compareTimestamps } from './timestampNormalizer';
import { safeGetItem, safeSetItem } from './safeStorage';

const DEBUG = env.features.enableDebugMode;

/**
 * Utilitário para padronizar a estrutura de dados de sessões
 * Resolve inconsistências entre diferentes formatos de timestamp
 */

/**
 * Interface para sessão com campos opcionais para migração
 */
interface RawSession {
  id: string;
  patientId: string;
  date: string;
  time: string;
  content: string;
  attachments?: any[];
  createdAt?: string;
  updatedAt?: string;
  created_at?: string;
  updated_at?: string;
  deleted?: boolean;
}

/**
 * Normaliza uma sessão para o formato padrão
 */
export function normalizeSession(rawSession: RawSession): Session {
  const now = normalizeTimestamp(new Date());
  
  // Migrar e normalizar timestamps
  const migratedSession = migrateTimestamps(rawSession);
  const createdAt = normalizeTimestamp(migratedSession.createdAt || now);
  const updated_at = normalizeTimestamp(migratedSession.updated_at || migratedSession.updatedAt || now);
  
  // Validar e corrigir attachments
  let attachments: any[] = [];
  if (Array.isArray(rawSession.attachments)) {
    attachments = rawSession.attachments.map(att => ({
      id: att.id || `att_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      sessionId: rawSession.id,
      name: att.name || 'Anexo sem nome',
      type: att.type || 'unknown',
      url: att.url || '',
      createdAt: att.createdAt || now
    }));
  }
  
  const normalizedSession: Session = {
    id: rawSession.id,
    patientId: rawSession.patientId,
    date: normalizeTimestamp(rawSession.date),
    time: rawSession.time, // Manter time como string para horários
    content: rawSession.content || '',
    attachments,
    createdAt,
    updated_at,
    deleted: Boolean(rawSession.deleted)
  };
  
  if (DEBUG) {
    console.log('Sessão normalizada:', {
      original: rawSession,
      normalized: normalizedSession
    });
  }
  
  return normalizedSession;
}

/**
 * Normaliza um array de sessões
 */
export function normalizeSessions(rawSessions: RawSession[]): Session[] {
  if (!Array.isArray(rawSessions)) {
    if (DEBUG) console.warn('normalizeSessions: entrada não é um array, retornando array vazio');
    return [];
  }
  
  return rawSessions
    .filter(session => session && session.id && session.patientId)
    .map(normalizeSession);
}

/**
 * Normaliza sessões agrupadas por paciente
 */
export function normalizeSessionsMap(rawSessionsMap: Record<string, RawSession[]>): Record<string, Session[]> {
  const normalizedMap: Record<string, Session[]> = {};
  
  Object.keys(rawSessionsMap).forEach(patientId => {
    if (Array.isArray(rawSessionsMap[patientId])) {
      normalizedMap[patientId] = normalizeSessions(rawSessionsMap[patientId])
        .sort((a, b) => compareTimestamps(b.createdAt, a.createdAt));
    }
  });
  
  return normalizedMap;
}

/**
 * Converte sessões do formato Record para Array (para Supabase)
 */
export function sessionsMapToArray(sessionsMap: Record<string, Session[]>): Session[] {
  return Object.values(sessionsMap).flat();
}

/**
 * Converte sessões do formato Array para Record (para localStorage)
 */
export function sessionsArrayToMap(sessionsArray: Session[]): Record<string, Session[]> {
  const sessionsMap: Record<string, Session[]> = {};
  
  sessionsArray.forEach(session => {
    if (!sessionsMap[session.patientId]) {
      sessionsMap[session.patientId] = [];
    }
    sessionsMap[session.patientId].push(session);
  });
  
  // Ordenar sessões de cada paciente por data (mais recente primeiro)
  Object.keys(sessionsMap).forEach(patientId => {
    sessionsMap[patientId].sort((a, b) => 
      compareTimestamps(b.createdAt, a.createdAt)
    );
  });
  
  return sessionsMap;
}

/**
 * Valida se uma sessão tem todos os campos obrigatórios
 */
export function validateSession(session: any): session is Session {
  return (
    session &&
    typeof session.id === 'string' &&
    typeof session.patientId === 'string' &&
    typeof session.date === 'string' &&
    typeof session.time === 'string' &&
    typeof session.content === 'string' &&
    Array.isArray(session.attachments) &&
    typeof session.createdAt === 'string' &&
    typeof session.updated_at === 'string'
  );
}

/**
 * Remove campos duplicados/obsoletos de uma sessão
 */
export function cleanSessionFields(session: any): Session {
  const cleaned = normalizeSession(session);
  
  // Remover campos obsoletos se existirem
  const cleanedSession = { ...cleaned };
  delete (cleanedSession as any).created_at;
  delete (cleanedSession as any).updatedAt;
  
  return cleanedSession;
}

/**
 * Migra dados de sessões do formato antigo para o novo
 */
export function migrateSessionsData(): void {
  try {
    const sessionsData = safeGetItem('psicosys_sessions');
    if (!sessionsData) return;
    
    const parsed = JSON.parse(sessionsData);
    let normalizedSessions: any;
    
    if (Array.isArray(parsed)) {
      normalizedSessions = normalizeSessions(parsed);
    } else if (typeof parsed === 'object') {
      // Converter mapa para array e normalizar
      const sessionsArray = sessionsMapToArray(parsed);
      normalizedSessions = sessionsArrayToMap(normalizeSessions(sessionsArray));
    } else {
      if (DEBUG) console.warn('Formato de dados de sessões desconhecido');
      return;
    }

    safeSetItem('psicosys_sessions', JSON.stringify(normalizedSessions));
  } catch (error) {
    if (DEBUG) console.error('Erro ao migrar dados de sessões:', error);
  }
}

/**
 * Verifica e corrige inconsistências nos dados de sessões
 */
export function validateAndFixSessions(): { fixed: number; errors: string[] } {
  const errors: string[] = [];
  let fixed = 0;
  
  try {
    const sessionsData = safeGetItem('psicosys_sessions');
    if (!sessionsData) return { fixed, errors };
    
    const parsed = JSON.parse(sessionsData);
    let validSessions: any = parsed;
    
    if (Array.isArray(parsed)) {
      const normalized = normalizeSessions(parsed);
      validSessions = normalized;
    } else if (typeof parsed === 'object') {
      // Validar cada sessão no mapa
      const sessionsArray = sessionsMapToArray(parsed);
      const validatedArray = sessionsArray.filter(session => {
        const isValid = validateSession(session);
        if (!isValid) {
          errors.push(`Sessão inválida: ${JSON.stringify(session)}`);
        }
        return isValid;
      });
      validSessions = sessionsArrayToMap(validatedArray);
    }

    safeSetItem('psicosys_sessions', JSON.stringify(validSessions));
  } catch (error) {
    if (DEBUG) console.error('Erro ao validar/corrigir sessões:', error);
  }

  return { fixed, errors };
}